import { Component, OnDestroy, OnInit } from '@angular/core';
import { CdkDragDrop, CdkDragEnd, CdkDragStart, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Subject, of, takeUntil } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { ConfigService } from 'src/app/config/config.service';
import { SplashscreenService } from 'src/app/shared/services/splashscreen.service';
import { DashboardContextService } from '../../data-access/dashboard-context.service';
import { DashboardLayoutService } from '../../data-access/dashboard-layout.service';
import { DashboardAccess, DashboardLayoutWidget, DashboardLegacyRow, DashboardWidgetDefinition, DASHBOARD_WIDGET_DEFINITIONS, DASHBOARD_WIDGET_TITLES } from '../../utils/dashboard.models';

@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrls: ['./dashboard-page.component.scss']
})
export class DashboardPageComponent implements OnInit, OnDestroy {
  widgets: DashboardLayoutWidget[] = [];
  widgetColumns: DashboardLayoutWidget[][] = [[], []];
  activeDragPlaceholderHeight: number | null = null;
  readonly dropListIds = ['dashboard-col-0', 'dashboard-col-1'];
  availableWidgets: DashboardWidgetDefinition[] = [];
  readonly dragStartDelay = { touch: 180, mouse: 0 };
  private access: DashboardAccess = { hasCapex: false, hasOpex: false, hasCombinedFromRights: false };
  private hasAccessLoaded = false;
  private hasLayoutLoaded = false;
  private hasMarkedDashboardShellReady = false;
  private readonly saveRequests$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly layoutService: DashboardLayoutService,
    private readonly contextService: DashboardContextService,
    private readonly configService: ConfigService,
    private readonly splashscreenService: SplashscreenService
  ) { }

  ngOnInit(): void {
    this.splashscreenService.startDashboardInit();
    this.contextService.refresh();

    this.contextService.access$
      .pipe(takeUntil(this.destroy$))
      .subscribe(access => {
        this.access = access;
        this.rebuildAvailableWidgets();
        this.setWidgets(this.widgets.filter(widget => this.isWidgetAllowedById(widget.id)));
      });

    this.contextService.accessResolved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((resolved) => {
        this.hasAccessLoaded = resolved;
        this.tryMarkDashboardShellReady();
      });

    this.layoutService.getLayout()
      .pipe(takeUntil(this.destroy$))
      .subscribe(layout => {
        this.hasLayoutLoaded = true;
        this.setWidgets(layout.selectedWidgets);
        this.rebuildAvailableWidgets();
        this.tryMarkDashboardShellReady();
      });

    this.saveRequests$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        switchMap(() => {
          const selectedWidgets = this.widgets.filter(widget => this.isWidgetAllowedById(widget.id));
          return this.layoutService.saveLayout({
            selectedWidgets,
            rows: this.buildRowsForSave()
          }).pipe(
            catchError(() => of(null))
          );
        })
      )
      .subscribe();
  }

  onDrop(event: CdkDragDrop<DashboardLayoutWidget[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.syncWidgetsFromColumns();
    this.queueLayoutSave();
  }

  onDragStarted(event: CdkDragStart<DashboardLayoutWidget>): void {
    const element = event?.source?.element?.nativeElement as HTMLElement | undefined;
    if (!element) {
      this.activeDragPlaceholderHeight = 48;
      return;
    }

    const height = Math.ceil(element.getBoundingClientRect().height);
    this.activeDragPlaceholderHeight = Math.max(height, 48);
  }

  onDragEnded(_: CdkDragEnd<DashboardLayoutWidget>): void {
    this.activeDragPlaceholderHeight = null;
  }

  isSelected(id: string): boolean {
    return this.widgets.some(widget => widget.id === id);
  }

  toggleWidget(id: string, enabled: boolean): void {
    if (enabled) {
      if (!this.isSelected(id) && this.isWidgetAllowedById(id)) {
        this.setWidgets([...this.widgets, { id }]);
        this.queueLayoutSave();
      }
      return;
    }

    this.setWidgets(this.widgets.filter(widget => widget.id !== id));
    this.queueLayoutSave();
  }

  trackByWidgetId(_: number, widget: DashboardLayoutWidget): string {
    return widget.id;
  }

  get availableWidgetCount(): number {
    return this.availableWidgets.length;
  }

  get selectedAvailableWidgetCount(): number {
    return this.availableWidgets.filter(widget => this.isSelected(widget.id)).length;
  }

  get manageDashboardLabel(): string {
    return `Manage Dashboard (${this.selectedAvailableWidgetCount} of ${this.availableWidgetCount} selected)`;
  }

  getWidgetTitle(id: string): string {
    return DASHBOARD_WIDGET_TITLES[id] || id;
  }

  private rebuildAvailableWidgets(): void {
    this.availableWidgets = DASHBOARD_WIDGET_DEFINITIONS.filter(widget => this.isWidgetAllowed(widget));
  }

  private setWidgets(widgets: DashboardLayoutWidget[]): void {
    this.widgets = [...widgets];
    this.rebuildColumnsFromWidgets();
  }

  private rebuildColumnsFromWidgets(): void {
    const left: DashboardLayoutWidget[] = [];
    const right: DashboardLayoutWidget[] = [];

    this.widgets.forEach((widget, index) => {
      if (index % 2 === 0) {
        left.push(widget);
      } else {
        right.push(widget);
      }
    });

    this.widgetColumns = [left, right];
  }

  private syncWidgetsFromColumns(): void {
    const [left, right] = this.widgetColumns;
    const maxLength = Math.max(left.length, right.length);
    const merged: DashboardLayoutWidget[] = [];

    for (let index = 0; index < maxLength; index++) {
      if (left[index]) {
        merged.push(left[index]);
      }

      if (right[index]) {
        merged.push(right[index]);
      }
    }

    this.widgets = merged;
  }

  private queueLayoutSave(): void {
    this.saveRequests$.next();
  }

  private buildRowsForSave(): DashboardLegacyRow[] {
    const [leftColumn, rightColumn] = this.widgetColumns;
    const left = (leftColumn || []).filter(widget => this.isWidgetAllowedById(widget.id));
    const right = (rightColumn || []).filter(widget => this.isWidgetAllowedById(widget.id));
    const now = Date.now();

    return [{
      columns: [
        {
          cid: 'col-0',
          styleClass: 'col-md-5',
          widgets: left.map((widget, index) => ({
            type: widget.id,
            title: this.getWidgetTitle(widget.id),
            wid: `wid-0-${index}-${now}`,
            config: {}
          }))
        },
        {
          cid: 'col-1',
          styleClass: 'col-md-7',
          widgets: right.map((widget, index) => ({
            type: widget.id,
            title: this.getWidgetTitle(widget.id),
            wid: `wid-1-${index}-${now}`,
            config: {}
          }))
        }
      ]
    }];
  }

  private isWidgetAllowedById(id: string): boolean {
    const definition = DASHBOARD_WIDGET_DEFINITIONS.find(widget => widget.id === id);
    if (!definition) {
      return false;
    }

    return this.isWidgetAllowed(definition);
  }

  private isWidgetAllowed(widget: DashboardWidgetDefinition): boolean {
    const dashboard = this.configService.dashboard();

    if (widget.featureFlag) {
      const flagValue = dashboard[widget.featureFlag];
      if (!flagValue) {
        return false;
      }
    }

    if (!this.access.hasCapex && !this.access.hasOpex) {
      return true;
    }

    if (widget.accessGroup === 'OPEX') {
      if (this.access.hasOpex || this.access.hasCombinedFromRights) {
        return true;
      }

      return !!dashboard.enableDashboardForEicAssetMap;
    }

    return this.access.hasCapex || this.access.hasOpex;
  }

  ngOnDestroy(): void {
    if (!this.hasMarkedDashboardShellReady) {
      this.splashscreenService.hideImmediately();
    }

    this.saveRequests$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryMarkDashboardShellReady(): void {
    if (this.hasMarkedDashboardShellReady || !this.hasAccessLoaded || !this.hasLayoutLoaded) {
      return;
    }

    this.hasMarkedDashboardShellReady = true;
    this.splashscreenService.markDashboardShellReady();
  }
}
